import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Download, FileText, Trash2, Pencil } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function MyBunks() {

  const { toast } = useToast();

  const [bunks, setBunks] = useState<any[]>([]);
  const [bunkName, setBunkName] = useState("");
  const [bunkUrl, setBunkUrl] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [editing, setEditing] = useState<any | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchBunks = async () => {

    const res = await fetch("/api/bunks");

    const data = await res.json();

    setBunks(Array.isArray(data) ? data : []);

  };

  useEffect(() => {

    fetchBunks();

  }, []);

  const isValidUrl = (url: string) => {

    try {

      new URL(url);

      return true;

    } catch {

      return false;

    }

  };

  const handleSave = async () => {

    if (!bunkName.trim()) {

      toast({ variant: "destructive", title: "Enter bunk name" });

      return;

    }

    if (bunkUrl && !isValidUrl(bunkUrl)) {

      toast({ variant: "destructive", title: "Enter valid URL" });

      return;

    }

    await fetch("/api/bunks", {

      method: "POST",

      headers: {
        "Content-Type": "application/json"
      },

      body: JSON.stringify({
        bunkName,
        bunkUrl
      })

    });

    setBunkName("");
    setBunkUrl("");

    toast({ title: "Bunk created successfully" });

    fetchBunks();

  };

  const confirmDelete = async () => {

    if (!deleteId) return;

    await fetch(`/api/bunks/${deleteId}`, {

      method: "DELETE"

    });

    toast({ title: "Bunk deleted" });

    setDeleteId(null);

    fetchBunks();

  };

  const handleUpdate = async () => {

    if (!editing) return;

    await fetch(`/api/bunks/${editing._id}`, {

      method: "PUT",

      headers: {
        "Content-Type": "application/json"
      },

      body: JSON.stringify(editing)

    });

    setEditing(null);

    toast({ title: "Bunk updated" });

    fetchBunks();

  };

  const filtered = bunks.filter((b) =>
    b.bunkName?.toLowerCase().includes(search.toLowerCase())
  );

  const start = (page - 1) * limit;

  const paginated = filtered.slice(start, start + limit);

  const totalPages = Math.ceil(filtered.length / limit) || 1;

  const totalBunks = bunks.length;
  const activeBunks = bunks.length;
  const recentBunks = bunks.slice(0, 3).length;
  const systemActivity = bunks.length * 2;

  const exportCSV = () => {

    const headers = [
      "SI No",
      "Bunk Name",
      "Bunk URL",
      "Created By",
      "Created At"
    ];

    const rows = bunks.map((b, i) => [

      i + 1,

      b.bunkName,

      b.bunkUrl,

      b.createdBy || "Super Admin",

      new Date(b.createdAt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    ]);

    const csv = [headers, ...rows]
      .map(r => r.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });

    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);

    link.download = "bunks.csv";

    link.click();

  };

  const exportPDF = () => {

    const doc = new jsPDF();

    autoTable(doc, {

      head: [["SI No", "Bunk Name", "Bunk URL", "Created By", "Created At"]],

      body: bunks.map((b, i) => [

        i + 1,

        b.bunkName,

        b.bunkUrl,

        b.createdBy || "Super Admin",

        new Date(b.createdAt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

      ])

    });

    doc.save("bunks.pdf");

  };

  return (

    <div className="space-y-6">

      <h1 className="text-3xl font-bold">My Bunks</h1>

      {/* Analytics Cards */}

      <div className="grid md:grid-cols-4 gap-4">

        <Card className="transition hover:scale-105">

          <CardContent className="pt-6">

            <p>Total Bunks</p>

            <p className="text-3xl font-bold">{totalBunks}</p>

          </CardContent>

        </Card>

        <Card className="transition hover:scale-105">

          <CardContent className="pt-6">

            <p>Active Bunks</p>

            <p className="text-3xl font-bold">{activeBunks}</p>

          </CardContent>

        </Card>

        <Card className="transition hover:scale-105">

          <CardContent className="pt-6">

            <p>Recently Created</p>

            <p className="text-3xl font-bold">{recentBunks}</p>

          </CardContent>

        </Card>

        <Card className="transition hover:scale-105">

          <CardContent className="pt-6">

            <p>System Activity</p>

            <p className="text-3xl font-bold">{systemActivity}</p>

          </CardContent>

        </Card>

      </div>

      {/* Create */}

      <Card className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white">

        <CardHeader>

          <CardTitle>Create Bunks</CardTitle>

        </CardHeader>

        <CardContent className="flex gap-4">

          <Input
            className="text-black"
            placeholder="Add bunk name"
            value={bunkName}
            onChange={(e) => setBunkName(e.target.value)}
          />

          <Input
            className="text-black"
            placeholder="Add bunk URL"
            value={bunkUrl}
            onChange={(e) => setBunkUrl(e.target.value)}
          />

          <Button onClick={handleSave}>
            Save
          </Button>

        </CardContent>

      </Card>

      {/* Table */}

      <Card>

        <CardHeader className="flex justify-between">

          <div className="flex gap-2">

            <select
              className="border p-1"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >

              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>

            </select>

          </div>

          <div className="flex gap-2">

            <Input
              placeholder="Filter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="outline" onClick={exportCSV}>
              <Download size={16}/> CSV
            </Button>

            <Button variant="outline" onClick={exportPDF}>
              <FileText size={16}/> PDF
            </Button>

          </div>

        </CardHeader>

        <CardContent>

          <table className="w-full">

            <thead>

              <tr className="border-b">

                <th>SI</th>
                <th>Bunk Name</th>
                <th>Bunk URL</th>
                <th>User Log Details</th>
                <th>Action</th>

              </tr>

            </thead>

            <tbody>

              {paginated.map((b, i) => (

                <tr key={b._id} className="border-b hover:bg-gray-50">

                  <td>{start + i + 1}</td>

                  <td>{b.bunkName}</td>

                  <td className="text-blue-600">{b.bunkUrl}</td>

                  <td className="text-sm text-gray-500">

                    Created: {b.createdBy || "Super Admin"}{" "}
                    {new Date(b.createdAt).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

                  </td>

                  <td className="flex gap-2">

                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => setEditing(b)}
                    >
                      <Pencil size={16}/>
                    </Button>

                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => setDeleteId(b._id)}
                    >
                      <Trash2 size={16}/>
                    </Button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

          {/* Pagination */}

          <div className="flex justify-between mt-4">

            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </Button>

            <span>Page {page} / {totalPages}</span>

            <Button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>

          </div>

        </CardContent>

      </Card>

      {/* Edit Modal */}

      {editing && (

        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">

          <Card className="w-[400px]">

            <CardHeader>
              <CardTitle>Edit Bunk</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">

              <Input
                value={editing.bunkName}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    bunkName: e.target.value
                  })
                }
              />

              <Input
                value={editing.bunkUrl}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    bunkUrl: e.target.value
                  })
                }
              />

              <div className="flex justify-end gap-2">

                <Button
                  variant="outline"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>

                <Button onClick={handleUpdate}>
                  Save
                </Button>

              </div>

            </CardContent>

          </Card>

        </div>

      )}

      {/* Delete Modal */}

      {deleteId && (

        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">

          <Card className="w-[350px]">

            <CardHeader>
              <CardTitle>Confirm Delete</CardTitle>
            </CardHeader>

            <CardContent>

              <p>Are you sure you want to delete this bunk?</p>

              <div className="flex justify-end gap-2 mt-4">

                <Button
                  variant="outline"
                  onClick={() => setDeleteId(null)}
                >
                  Cancel
                </Button>

                <Button
                  variant="destructive"
                  onClick={confirmDelete}
                >
                  Delete
                </Button>

              </div>

            </CardContent>

          </Card>

        </div>

      )}

    </div>

  );

}
